import { FormikErrors, FormikProps, getIn, useFormikContext, withFormik } from 'formik'
import { t } from 'i18next'
import React, { FormEventHandler, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { Body1, Heading6 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Button, IconButton } from '@ui/components/Button'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { CloseIcon } from '@ui/icons/Close.icon'
import { InfoIcon } from '@ui/icons/Info.icon'

import { EmailPattern, EnglishLettersPattern } from '@utils/formValidation'
import { isEmpty } from '@utils/isEmpty'

import { NameDto } from '@model/NameDto'
import { RegulatorOptionDto } from '@model/RegulatorOptionDto'

import { FieldGroup } from '@global/form/FieldGroup'
import { createFormField } from '@global/formField/FormField'
import { RawSelectFormField } from '@global/formField/FormSelectField'

import { Box } from '@shared/components/Box'
import { DropdownInput } from '@shared/components/Dropdown'
import { FieldWrapper } from '@shared/components/FieldWrapper'

import styles from '../CompanyDetailsStep.module.scss'

const DynamicFormField = createFormField()

export interface CompanyDetailsStep3FormValues {
  numberOfDirectors: number
  directors: Director[]
  numberOfSignatories: number
  signatories: Signatory[]
  numberOfTraders: number
  traders: Trader[]
}

interface Director {
  name: string
  street: string
  city: string
  state: string
  zipCode: string
  country: NameDto<string>
}

interface Signatory {
  name: string
  role: string
}

interface Trader {
  name: string
  role: string
  email: string
}

interface OuterProps {
  regulators: RegulatorOptionDto[]
  onSubmit(values: CompanyDetailsStep3FormValues): void
  submitLoading?: boolean
  initialValues: CompanyDetailsStep3FormValues | undefined
}

const CompanyDetailsStep3FormUI: React.FC<
  FormikProps<CompanyDetailsStep3FormValues> & OuterProps
> = (props) => {
  const { values, submitLoading, isSubmitting, isValid, regulators = [] } = props
  const { handleSubmit, setFieldValue } = props

  const { t } = useTranslation()
  const isArabic = useArabicSessionLanguage()

  const sizeOptions = Array.from({ length: 10 }, (_, i) => i + 1).map((v) => ({
    name: v.toString(),
    id: v,
  }))

  const handleSubmitForm: FormEventHandler = (event) => {
    event.preventDefault()
    handleSubmit()
  }

  const handleAddNumberOfDirectors = () => {
    setFieldValue('numberOfDirectors', values.numberOfDirectors + 1)
  }

  const handleAddNumberOfSignatories = () => {
    setFieldValue('numberOfSignatories', values.numberOfSignatories + 1)
  }

  const handleAddNumberOfTraders = () => {
    setFieldValue('numberOfTraders', values.numberOfTraders + 1)
  }

  return (
    <React.Fragment>
      <Form onSubmit={handleSubmitForm} className={styles.form}>
        <div>
          <Heading6 className='pb-4' medium>
            {t('Sign up.Number of Directors')}
          </Heading6>
          <FieldWrapper>
            <RawSelectFormField
              name={'numberOfDirectors'}
              label={t('Sign up.Number of Directors')}
              required
              showLabel
              options={sizeOptions}
              hasCheckMark={false}
              dropdownListVariant='compact'
            />
            {Array.from({ length: values.numberOfDirectors }).map((_, id) => (
              <DirectorFields
                id={id}
                name={`directors[${[id]}]`}
                regulators={regulators}
                key={id}
              />
            ))}
            <Button.Secondary size='sm' onClick={handleAddNumberOfDirectors}>
              {t('Sign up.+ Add Director')}
            </Button.Secondary>
          </FieldWrapper>
        </div>

        <div className='pb-4'>
          <Heading6 className='pb-4' medium>
            {t('Sign up.Authorised Signatories')}{' '}
            <Tooltip
              content={t(
                "Sign up.Person who's been given the right to sign documents and to make deposits and withdrawals on behalf of the authorising organisation"
              )}
            >
              <ActionIcon icon={InfoIcon} size='x20' />
            </Tooltip>
          </Heading6>
          <FieldWrapper>
            <RawSelectFormField
              showLabel
              name='numberOfSignatories'
              label={t('Sign up.Number of Signatories')}
              required
              options={sizeOptions}
              hasCheckMark={false}
              dropdownListVariant='compact'
            />
            {Array.from({ length: values.numberOfSignatories }).map((_, id) => (
              <SignatoryFields id={id} name={`signatories[${[id]}]`} key={id} />
            ))}
            <Button.Secondary size='sm' onClick={handleAddNumberOfSignatories}>
              {t('Sign up.+ Add Signatory')}
            </Button.Secondary>
          </FieldWrapper>
        </div>

        <div className='pb-4'>
          <Heading6 className='pb-4' medium>
            {t('Sign up.Authorised Traders')}{' '}
            <Tooltip
              content={t(
                'Sign up.The following persons are employees of the above-mentioned company and are hereby authorised to execute trades on behalf of the company.'
              )}
            >
              <ActionIcon icon={InfoIcon} size='x20' />
            </Tooltip>
          </Heading6>
          <FieldWrapper>
            <RawSelectFormField
              showLabel
              name='numberOfTraders'
              label={t('Sign up.Number of Traders')}
              required
              options={sizeOptions}
              hasCheckMark={false}
              dropdownListVariant='compact'
            />
            {Array.from({ length: values.numberOfTraders }).map((_, id) => (
              <TraderFields id={id} name={`traders[${[id]}]`} key={id} />
            ))}
            <Button.Secondary size='sm' onClick={handleAddNumberOfTraders}>
              {t('Sign up.+ Add Trader')}
            </Button.Secondary>
          </FieldWrapper>
        </div>

        <div className={styles.button}>
          <Button.Primary
            size='lg'
            type='submit'
            fullWidth
            iconRight={isArabic ? ArrowIcon.Left : ArrowIcon.Right}
            loading={submitLoading}
            disabled={isSubmitting || !isValid}
          >
            {t('Next')}
          </Button.Primary>
        </div>
      </Form>
    </React.Fragment>
  )
}

interface DirectorFieldsProps {
  id: number
  name: string
  regulators: RegulatorOptionDto[]
}

const DirectorFields: React.FC<DirectorFieldsProps> = (props) => {
  const { id, name, regulators } = props
  const countryOptions = regulators.map((regulator) => regulator.country)

  const { t } = useTranslation()
  const { validateStreet, validateCity, validateState, validateCountry, validateZipCode } =
    useFormValidation()

  const { values, setFieldValue } = useFormikContext<CompanyDetailsStep3FormValues>()
  const [countrySearch, setCountrySearch] = useState('')

  const handleRemoveNumberOfDirectors = () => {
    const numberOfDirectors = values.directors.filter((_, index) => index !== id)
    setFieldValue('directors', numberOfDirectors)
    setFieldValue('numberOfDirectors', numberOfDirectors.length)
  }

  return (
    <React.Fragment>
      <Heading6
        className='pb-4 is-flex is-justify-content-space-between is-align-items-center'
        medium
      >
        <Body1>
          <Box flex align='center'>
            <Box>
              {t('Sign up.Director')} {id + 1}
            </Box>
            <Box ml='x4'>
              <Tooltip
                content={t(
                  'Sign up.Any Shareholder who owns more than 25% of the shares must provide their personal information below (for two active directors, or one director for sole directors)'
                )}
              >
                <ActionIcon icon={InfoIcon} size='x20' />
              </Tooltip>
            </Box>
          </Box>
        </Body1>{' '}
        {id >= 1 && <IconButton.Ghost icon={CloseIcon} onClick={handleRemoveNumberOfDirectors} />}
      </Heading6>
      <DynamicFormField
        name={`${name}.name`}
        label={t('Sign up.Name')}
        type='text'
        max={100}
        min={1}
        required
        showLabel
        pattern='.*\S+.*'
      />

      <DropdownInput
        showLabel
        name={`${name}.country`}
        value={getIn(values, `${name}.country.id`)}
        label={t('Sign up.Country of Residence')}
        required
        validate={validateCountry}
        options={(countryOptions || []).map((country) => ({
          id: String(country.id),
          name: country.name,
        }))}
        hasCheckMark={false}
        searchInput={{
          value: countrySearch,
          handleChange: (e) => setCountrySearch(e.target.value),
          handleClear: () => setCountrySearch(''),
        }}
        onChange={(option) => {
          setFieldValue(`${name}.country`, option)
          setCountrySearch('')
        }}
      />
      <DynamicFormField
        name={`${name}.street`}
        label={t('Sign up.Street Name, Street Number, Apt. Number')}
        validate={validateStreet}
        required
        showLabel
        pattern='.*\S+.*'
      />
      <DynamicFormField
        name={`${name}.city`}
        label={t('Sign up.City')}
        validate={validateCity}
        required
        showLabel
        pattern='.*\S+.*'
      />
      <FieldGroup>
        <DynamicFormField
          name={`${name}.state`}
          label={t('Sign up.State/Region')}
          validate={validateState}
          required
          showLabel
          pattern='.*\S+.*'
        />
        <DynamicFormField
          name={`${name}.zipCode`}
          label={t('Sign up.ZIP/Postal Code')}
          validate={validateZipCode}
          type='text'
          required
          showLabel
          pattern='.*\S+.*'
        />
      </FieldGroup>
    </React.Fragment>
  )
}

const useFormValidation = () => {
  const { t } = useTranslation()

  return createFormValidators(t)
}

const createFormValidators = (getTranslation: (key: string) => string) => {
  const validateStreet = (value: string) => {
    if (isEmpty(value)) {
      return `${getTranslation('Validation.Required')}`
    }
    if (!EnglishLettersPattern.test(value)) {
      return `${getTranslation('Validation.Invalid street')}`
    }
    return undefined
  }

  const validateCity = (value: string) => {
    if (isEmpty(value)) {
      return `${getTranslation('Validation.Required')}`
    }
    if (!EnglishLettersPattern.test(value)) {
      return `${getTranslation('Validation.Invalid city')}`
    }
  }

  const validateState = (value: string) => {
    if (isEmpty(value)) {
      return `${getTranslation('Validation.Required')}`
    }
    if (!EnglishLettersPattern.test(value)) {
      return `${getTranslation('Validation.Invalid state')}`
    }
  }

  const validateCountry = (value: string) => {
    if (isEmpty(value)) {
      return `${getTranslation('Validation.Required')}`
    }
  }

  const validateZipCode = (value: string) => {
    if (isEmpty(value)) {
      return `${getTranslation('Validation.Required')}`
    }
    if (value.length > 32) {
      return `${getTranslation('Validation.Maximum character limit is 32')}`
    }
    if (!EnglishLettersPattern.test(value)) {
      return `${getTranslation('Validation.Invalid postal code')}`
    }
  }

  return { validateStreet, validateCity, validateState, validateZipCode, validateCountry }
}

interface SignatoryFieldsProps {
  id: number
  name: string
}

const SignatoryFields: React.FC<SignatoryFieldsProps> = (props) => {
  const { id, name } = props

  const { t } = useTranslation()

  const { values, setFieldValue } = useFormikContext<CompanyDetailsStep3FormValues>()

  const handleRemoveNumberOfSignatories = () => {
    const numberOfSignatories = values.signatories.filter((_, index) => index !== id)
    setFieldValue('signatories', numberOfSignatories)
    setFieldValue('numberOfSignatories', numberOfSignatories.length)
  }

  return (
    <React.Fragment>
      <Heading6
        className='pb-4 is-flex is-justify-content-space-between is-align-items-center'
        medium
      >
        <Body1>
          {t('Sign up.Signatory')} {id + 1}
        </Body1>{' '}
        {id >= 1 && <IconButton.Ghost icon={CloseIcon} onClick={handleRemoveNumberOfSignatories} />}
      </Heading6>
      <FieldWrapper>
        <DynamicFormField
          name={`${name}.name`}
          label={t('Sign up.Name')}
          type='text'
          max={100}
          min={1}
          required
          showLabel
          pattern='.*\S+.*'
        />
        <DynamicFormField
          name={`${name}.role`}
          label={t('Sign up.Role in Company')}
          type='text'
          required
          showLabel
          pattern='.*\S+.*'
        />
      </FieldWrapper>
    </React.Fragment>
  )
}

interface TraderFieldsProps {
  id: number
  name: string
}

const useValidateTraderFields = () => {
  const { t } = useTranslation()

  const validateEmail = (value: string) => {
    if (!value) {
      return `${t('Validation.Required')}`
    }
    if (value && !EmailPattern.test(value)) {
      return `${t('Validation.Invalid email address')}`
    }
  }

  return [validateEmail]
}

const TraderFields: React.FC<TraderFieldsProps> = (props) => {
  const { id, name } = props

  const { t } = useTranslation()

  const { values, setFieldValue } = useFormikContext<CompanyDetailsStep3FormValues>()

  const handleRemoveNumberOfTraders = () => {
    const newTraders = values.traders.filter((_, index) => index !== id)
    setFieldValue('traders', newTraders)
    setFieldValue('numberOfTraders', newTraders.length)
  }

  const [validateEmail] = useValidateTraderFields()
  return (
    <React.Fragment>
      <Heading6
        className='pb-4 is-flex is-justify-content-space-between is-align-items-center'
        medium
      >
        <Body1>
          {t('Sign up.Trader')} {id + 1}{' '}
        </Body1>
        {id >= 1 && <IconButton.Ghost icon={CloseIcon} onClick={handleRemoveNumberOfTraders} />}
      </Heading6>
      <FieldWrapper>
        <DynamicFormField
          name={`${name}.name`}
          label={t('Sign up.Name')}
          type='text'
          max={100}
          min={1}
          required
          showLabel
          pattern='.*\S+.*'
        />
        <DynamicFormField
          name={`${name}.role`}
          label={t('Sign up.Role in Company')}
          type='text'
          required
          showLabel
          pattern='.*\S+.*'
        />
        <DynamicFormField
          name={`${name}.email`}
          label={t('Sign up.Email')}
          type='text'
          required
          showLabel
          validate={validateEmail}
        />
      </FieldWrapper>
    </React.Fragment>
  )
}

const createEmptyDirector = (): Director => ({
  name: '',
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: { id: '', name: '' },
})

const createEmptySignatory = (): Signatory => ({
  name: '',
  role: '',
})

const createEmptyTrader = (): Trader => ({
  name: '',
  role: '',
  email: '',
})

const createItemsForCount = <TItem,>(
  items: TItem[] = [],
  numberOfItems: number,
  createEmptyItem: () => TItem
): TItem[] => {
  const itemCount = Math.max(Number(numberOfItems) || 0, 0)

  return Array.from({ length: itemCount }, (_, id) => items[id] || createEmptyItem())
}

export const CompanyDetailsStep3Form = withFormik<OuterProps, CompanyDetailsStep3FormValues>({
  mapPropsToValues: ({ initialValues }) => {
    const numDirectors = initialValues?.numberOfDirectors || 1
    const numSignatories = initialValues?.numberOfSignatories || 1
    const numTraders = initialValues?.numberOfTraders || 1
    return {
      numberOfDirectors: numDirectors,
      directors: createItemsForCount(initialValues?.directors, numDirectors, createEmptyDirector),
      numberOfSignatories: numSignatories,
      signatories: createItemsForCount(
        initialValues?.signatories,
        numSignatories,
        createEmptySignatory
      ),
      numberOfTraders: numTraders,
      traders: createItemsForCount(initialValues?.traders, numTraders, createEmptyTrader),
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values: CompanyDetailsStep3FormValues) => {
    const errors: FormikErrors<CompanyDetailsStep3FormValues> = {}
    const required = t('Validation.Required')

    const formValidators = createFormValidators(t)

    if (!values.numberOfDirectors || values.numberOfDirectors < 1) {
      errors.numberOfDirectors = required
    }
    if (!values.numberOfSignatories || values.numberOfSignatories < 1) {
      errors.numberOfSignatories = required
    }
    if (!values.numberOfTraders || values.numberOfTraders < 1) {
      errors.numberOfTraders = required
    }

    if (values.numberOfDirectors > 0) {
      const directors = createItemsForCount(
        values.directors,
        values.numberOfDirectors,
        createEmptyDirector
      ).map((director) => {
        const directorErrors: FormikErrors<Director> = {}

        if (!director.name?.trim()) {
          directorErrors.name = required
        }

        const countryError = formValidators.validateCountry(director.country?.id || '')
        if (countryError) {
          directorErrors.country = countryError as FormikErrors<NameDto<string>>
        }

        const streetError = formValidators.validateStreet(director.street || '')
        if (streetError) directorErrors.street = streetError

        const cityError = formValidators.validateCity(director.city || '')
        if (cityError) directorErrors.city = cityError

        const stateError = formValidators.validateState(director.state || '')
        if (stateError) directorErrors.state = stateError

        const zipError = formValidators.validateZipCode(director.zipCode || '')
        if (zipError) directorErrors.zipCode = zipError

        return Object.keys(directorErrors).length > 0 ? directorErrors : undefined
      })

      if (directors.some(Boolean)) {
        errors.directors = directors as FormikErrors<Director>[]
      }
    }

    if (values.numberOfSignatories > 0) {
      const signatories = createItemsForCount(
        values.signatories,
        values.numberOfSignatories,
        createEmptySignatory
      ).map((signatory) => {
        const signatoryErrors: FormikErrors<Signatory> = {}

        if (!signatory.name?.trim()) {
          signatoryErrors.name = required
        }

        if (!signatory.role?.trim()) {
          signatoryErrors.role = required
        }

        return Object.keys(signatoryErrors).length > 0 ? signatoryErrors : undefined
      })

      if (signatories.some(Boolean)) {
        errors.signatories = signatories as FormikErrors<Signatory>[]
      }
    }

    if (values.numberOfTraders > 0) {
      const traders = createItemsForCount(
        values.traders,
        values.numberOfTraders,
        createEmptyTrader
      ).map((trader) => {
        const traderErrors: FormikErrors<Trader> = {}

        if (!trader.name?.trim()) {
          traderErrors.name = required
        }

        if (!trader.role?.trim()) {
          traderErrors.role = required
        }

        if (!trader.email?.trim()) {
          traderErrors.email = required
        } else if (trader.email && !EmailPattern.test(trader.email)) {
          traderErrors.email = t('Validation.Invalid email address')
        }

        return Object.keys(traderErrors).length > 0 ? traderErrors : undefined
      })

      if (traders.some(Boolean)) {
        errors.traders = traders as FormikErrors<Trader>[]
      }
    }

    return errors
  },
})(CompanyDetailsStep3FormUI)
